import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ModalBody from '../../Modal/ModalBody';
import Button from '../../Button/Button';
import SectionLoader from '../../Loader/SectionLoader';
import ProductImage from '../../LegacyOrder/ProductImage';
import { ReactComponent as ClockBlue } from '../../../assets/images/svg/clock-blue.svg';
import { ReactComponent as DownloadIcon } from '../../../assets/images/svg/download.svg';
import { getCurrentAccountUid } from '../../../store/selectors/accountSelectors';
import {
  selectPortfolioSingleTransaction,
  selectPortfolioSingleTransactionError,
} from '../../../store/selectors/portfolioTransactionsSelector';
import { selectIsLoadingByActionType } from '../../../store/selectors/loadingSelectors';
import { portfolioTransactionsSingleFetch } from '../../../store/actions/portfolio/portfolioActions';
import { PORTFOLIO_TRANSACTIONS_SINGLE_LOADING } from '../../../store/actions/portfolio/portfolioActionConstants';
import { formatSfhDateTime } from '../../../util/helpers/dateHelpers';
import { formatMoneyNumeral } from '../../../util/helpers/numeralHelpers';
import {
  NOT_AVAILABLE,
  FIELD_CAPTION_TYPE,
  getTransactionField,
} from './sellFromHomeTransactionHelpers';
import { pxToRem } from '../../../assets/styles/helper';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';

const SUBTITLE_COLOR = themeColors.colorTextPrimary;

// Only the detail rows scroll — between the SKU divider (#2) and the bottom
// links/CTA divider (#3). The SKU section and the footer stay fixed.
const ScrollRegion = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const HeroSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  gap: ${pxToRem(8)};
  padding: ${pxToRem(8)} ${pxToRem(16)} ${pxToRem(16)};
  margin: 0 ${pxToRem(-16)};
  border-bottom: 1px solid ${themeColors.colorBorderPrimary};
`;

const HeroImage = styled.div`
  > img {
    width: ${pxToRem(80)};
    height: ${pxToRem(80)};
    object-fit: contain;
    margin: 0;
  }
`;

const HeroCaption = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(16)};
  font-weight: 700;
  color: ${SUBTITLE_COLOR};
  text-align: center;
`;

const DetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${pxToRem(12)};
`;

const DetailRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${pxToRem(12)};
  padding: ${pxToRem(6)} 0;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
`;

const DetailLabel = styled.span`
  color: ${themeColors.colorTextSecondary};
  font-weight: 500;
`;

const DetailValue = styled.span`
  color: ${themeColors.colorTextPrimary};
  text-align: right;
`;

// Fixed bottom area below the scroll region; its top border is divider #3.
const BottomArea = styled.div`
  margin-top: auto;
  border-top: 1px solid ${themeColors.colorBorderPrimary};
  margin-left: ${pxToRem(-16)};
  margin-right: ${pxToRem(-16)};
  padding-left: ${pxToRem(16)};
  padding-right: ${pxToRem(16)};
`;

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${pxToRem(12)};
  padding: ${pxToRem(16)} 0;
`;

const LinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${pxToRem(6)};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorPrimary};
  text-decoration: underline;

  > svg {
    width: ${pxToRem(16)};
    height: ${pxToRem(16)};
  }
`;

const FooterRow = styled.div`
  display: flex;
  gap: ${pxToRem(12)};
  margin-top: ${pxToRem(8)};

  > button {
    flex: 1;
  }
`;

const DownloadButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${pxToRem(6)};

  > svg {
    width: ${pxToRem(16)};
    height: ${pxToRem(16)};
  }
`;

const FetchErrorText = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(13)};
  color: ${themeColors.colorError};
  text-align: center;
  margin: ${pxToRem(8)} 0 0;
`;

const SellFromHomeTransactionDetails = ({
  selectedHit,
  quantity,
  lockedSnapshot,
  handleClose,
  handleViewHistory,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const accountUid = useSelector(getCurrentAccountUid);
  const singleTransaction = useSelector(selectPortfolioSingleTransaction);
  const singleTransactionError = useSelector(
    selectPortfolioSingleTransactionError,
  );
  const isFetchingTransaction = useSelector(
    selectIsLoadingByActionType(PORTFOLIO_TRANSACTIONS_SINGLE_LOADING),
  );
  const transactionUid = lockedSnapshot?.transactionUid;

  useEffect(() => {
    if (!accountUid || !transactionUid) return;
    if (singleTransaction?.Uid === transactionUid) return;
    dispatch(portfolioTransactionsSingleFetch({ accountUid, transactionUid }));
  }, [accountUid, transactionUid, dispatch, singleTransaction?.Uid]);

  const decodeHtml = (str) => {
    if (!str) return str;
    const el = document.createElement('div');
    el.innerHTML = str;
    return el.textContent;
  };

  const fields = singleTransaction?.Fields ?? [];

  // When launched from the wizard we have the selected product (selectedHit);
  // when launched from Recent Transactions we only have a Uid, so fall back to
  // the product details carried in the fetched transaction fields.
  const productImageField = fields.find((f) => f.FormatType === 'ProductImage');
  const productField = fields.find((f) => f.FormatType === 'Product');

  const productCode = selectedHit?.Code ?? productImageField?.ProductCode;
  const productCaption = selectedHit
    ? `${decodeHtml(selectedHit?.WeightCaption) ?? ''} ${
        decodeHtml(selectedHit?.Caption) ?? ''
      }`.trim()
    : decodeHtml(productField?.ProductCaption) ?? '';

  // Show the backend timestamp exactly as received (no timezone conversion);
  // only fall back to the client submission time if the BE value is missing.
  const rawDate = singleTransaction?.DateTime || lockedSnapshot?.submittedAt;
  const transactedOn = rawDate ? formatSfhDateTime(rawDate) : NOT_AVAILABLE;

  const referenceField = getTransactionField(
    fields,
    FIELD_CAPTION_TYPE.REFERENCE_NUMBER,
  );
  const referenceNumber = referenceField?.Caption ?? NOT_AVAILABLE;

  const submitterField = getTransactionField(
    fields,
    FIELD_CAPTION_TYPE.SUBMITTER,
  );
  const submitter = submitterField?.Caption?.trim() || NOT_AVAILABLE;

  const summaryField = getTransactionField(fields, FIELD_CAPTION_TYPE.SUMMARY);
  const displayQuantity = summaryField?.AmountOrQuantity ?? quantity;

  const pricePerUnitField = getTransactionField(
    fields,
    FIELD_CAPTION_TYPE.PRICE_PER_UNIT,
  );
  const pricePerUnit =
    pricePerUnitField != null
      ? formatMoneyNumeral(pricePerUnitField.Amount)
      : NOT_AVAILABLE;

  const orderTotalField = getTransactionField(
    fields,
    FIELD_CAPTION_TYPE.ORDER_TOTAL,
  );
  const orderTotal =
    orderTotalField != null
      ? formatMoneyNumeral(orderTotalField.Amount)
      : NOT_AVAILABLE;

  const shippingFeeField = getTransactionField(
    fields,
    FIELD_CAPTION_TYPE.SHIPPING_FEE,
  );
  const shippingFee =
    shippingFeeField != null
      ? formatMoneyNumeral(shippingFeeField.Amount)
      : NOT_AVAILABLE;

  const insuranceFeeField = getTransactionField(
    fields,
    FIELD_CAPTION_TYPE.INSURANCE_FEE,
  );
  const insuranceFee =
    insuranceFeeField != null
      ? formatMoneyNumeral(insuranceFeeField.Amount)
      : NOT_AVAILABLE;

  const creditCardField = getTransactionField(
    fields,
    FIELD_CAPTION_TYPE.CREDIT_CARD,
  );
  const creditCard = creditCardField?.Caption
    ? `****${creditCardField.Caption}`
    : NOT_AVAILABLE;

  const preAuthField = getTransactionField(
    fields,
    FIELD_CAPTION_TYPE.PRE_AUTH_AMOUNT,
  );
  const preAuthAmount =
    preAuthField != null
      ? formatMoneyNumeral(preAuthField.Amount)
      : NOT_AVAILABLE;

  const statusLabel = singleTransaction?.StatusType
    ? t(
        `sellWizard.sfhWizard.transactionDetails.status_${singleTransaction.StatusType}`,
        {
          defaultValue: t(
            'sellWizard.sfhWizard.transactionDetails.statusRequestSubmitted',
          ),
        },
      )
    : t('sellWizard.sfhWizard.transactionDetails.statusRequestSubmitted');

  // Show skeletons whenever the stored transaction doesn't match the one we
  // need — covers the gap between History clearing Redux and the fetch
  // completing (isFetchingTransaction can be false for one render cycle).
  const showLoader =
    (isFetchingTransaction || singleTransaction?.Uid !== transactionUid) &&
    !singleTransactionError;

  // Returns `skeleton` while loading, otherwise the real `value`.
  const ph = (value, skeleton) => (showLoader ? skeleton : value);

  const onViewHistoryClick = () => {
    if (typeof handleViewHistory === 'function') handleViewHistory();
  };

  const handleDownloadPackingSlip = () => {
    // TODO(BE): wire up once the packing-slip download URL is exposed.
  };

  const handleDownloadShippingLabel = () => {
    // TODO(BE): wire up once the shipping-label download URL is exposed.
  };

  return (
    <SectionLoader isLoading={showLoader}>
      <ModalBody>
        <HeroSection>
          <HeroImage>
            <ProductImage productCode={productCode} width={80} height={80} />
          </HeroImage>
          <HeroCaption>{productCaption}</HeroCaption>
        </HeroSection>

        <ScrollRegion>
          <DetailsSection>
            <DetailRow>
              <DetailLabel>
                {t('sellWizard.sfhWizard.transactionDetails.summaryLabel')}
              </DetailLabel>
              <DetailValue>
                {t('sellWizard.sfhWizard.transactionDetails.summaryValue', {
                  count: displayQuantity,
                })}
              </DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>
                {t('sellWizard.sfhWizard.transactionDetails.transactedOn')}
              </DetailLabel>
              <DetailValue>
                {ph(transactedOn, formatSfhDateTime(new Date()))}
              </DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>
                {t('sellWizard.sfhWizard.transactionDetails.referenceNumber')}
              </DetailLabel>
              <DetailValue>{ph(referenceNumber, 'SFH-——————')}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>
                {t('sellWizard.sfhWizard.transactionDetails.status')}
              </DetailLabel>
              <DetailValue>
                {ph(
                  statusLabel,
                  t(
                    'sellWizard.sfhWizard.transactionDetails.statusRequestSubmitted',
                  ),
                )}
              </DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>
                {t('sellWizard.sfhWizard.transactionDetails.submitter')}
              </DetailLabel>
              <DetailValue>{ph(submitter, '——')}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>
                {t('sellWizard.sfhWizard.transactionDetails.pricePerUnit')}
              </DetailLabel>
              <DetailValue>
                {ph(
                  pricePerUnit,
                  lockedSnapshot?.unitPrice != null
                    ? formatMoneyNumeral(lockedSnapshot.unitPrice)
                    : '$—.——',
                )}
              </DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>
                {t('sellWizard.sfhWizard.transactionDetails.orderTotal')}
              </DetailLabel>
              <DetailValue>
                {ph(
                  orderTotal,
                  lockedSnapshot?.payout != null
                    ? formatMoneyNumeral(lockedSnapshot.payout)
                    : '$—.——',
                )}
              </DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>
                {t('sellWizard.sfhWizard.transactionDetails.preAuthAmount')}
              </DetailLabel>
              <DetailValue>{ph(preAuthAmount, '$—.——')}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>
                {t('sellWizard.sfhWizard.transactionDetails.shippingFee')}
              </DetailLabel>
              <DetailValue>{ph(shippingFee, '$—.——')}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>
                {t('sellWizard.sfhWizard.transactionDetails.insuranceFee')}
              </DetailLabel>
              <DetailValue>{ph(insuranceFee, '$—.——')}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>
                {t('sellWizard.sfhWizard.transactionDetails.creditCard')}
              </DetailLabel>
              <DetailValue>{ph(creditCard, '****——')}</DetailValue>
            </DetailRow>
          </DetailsSection>
        </ScrollRegion>

        <BottomArea>
          <LinkRow>
            <LinkButton
              type="button"
              onClick={onViewHistoryClick}
              data-cy="link-sfh-view-history"
            >
              <ClockBlue />
              {t('sellWizard.sfhWizard.transactionDetails.viewHistory')}
            </LinkButton>
            <LinkButton
              type="button"
              onClick={handleDownloadPackingSlip}
              data-cy="link-sfh-download-packing-slip"
            >
              <DownloadIcon />
              {t('sellWizard.sfhWizard.transactionDetails.downloadPackingSlip')}
            </LinkButton>
          </LinkRow>

          {singleTransactionError && (
            <FetchErrorText data-cy="text-sfh-transaction-fetch-error">
              {t('sellWizard.sfhWizard.transactionDetails.fetchFailed')}
            </FetchErrorText>
          )}

          <FooterRow>
            <Button
              variant="primaryOutlined"
              textTransform="none"
              onClick={handleClose}
              data-cy="button-sfh-transaction-close"
            >
              {t('sellWizard.sfhWizard.transactionDetails.close')}
            </Button>
            <Button
              variant="primaryOutlined"
              textTransform="none"
              onClick={handleDownloadShippingLabel}
              data-cy="button-sfh-download-shipping-label"
            >
              <DownloadButtonContent>
                <DownloadIcon />
                {t(
                  'sellWizard.sfhWizard.transactionDetails.downloadShippingLabel',
                )}
              </DownloadButtonContent>
            </Button>
          </FooterRow>
        </BottomArea>
      </ModalBody>
    </SectionLoader>
  );
};

SellFromHomeTransactionDetails.propTypes = {
  selectedHit: PropTypes.shape({
    Code: PropTypes.string,
    Caption: PropTypes.string,
    WeightCaption: PropTypes.string,
  }),
  quantity: PropTypes.number,
  lockedSnapshot: PropTypes.shape({
    transactionUid: PropTypes.string,
    submittedAt: PropTypes.string,
    unitPrice: PropTypes.number,
    payout: PropTypes.number,
  }),
  handleClose: PropTypes.func,
  handleViewHistory: PropTypes.func,
};

export default SellFromHomeTransactionDetails;
